/**
 * GOVX Collection List Component
 * Handles tab switching and load more pagination using section rendering
 */

// Track initialized sections to prevent duplicate listeners
const initializedSections = new Set();

function initGovxCollectionList() {
  // Find all GOVX collection list sections
  const sections = document.querySelectorAll('.section.govx-collection-list');

  sections.forEach((sectionEl) => {
    const sectionId = sectionEl.dataset.sectionId || extractSectionId(sectionEl);
    if (!sectionId) return;
    
    // Skip if already initialized
    if (initializedSections.has(sectionId)) return;
    initializedSections.add(sectionId);

    // Tab switching - use event delegation to handle dynamic content
    sectionEl.addEventListener('click', async function(e) {
      const tab = e.target.closest(`.govx-collection-tab-${sectionId}`);
      if (!tab) return;
      
      e.preventDefault();
      
      const collectionHandle = tab.dataset.collectionHandle;
      if (!collectionHandle) return;

      // Update active tab state
      const allTabs = sectionEl.querySelectorAll(`.govx-collection-tab-${sectionId}`);
      allTabs.forEach((t) => {
        t.classList.remove('tw-bg-white', 'tw-border-primary', 'tw-text-primary');
        t.classList.add('tw-bg-transparent', 'tw-border-[rgba(0,0,0,0.6)]', 'tw-text-[rgba(0,0,0,0.6)]');
        t.setAttribute('aria-selected', 'false');
      });
      
      tab.classList.remove('tw-bg-transparent', 'tw-border-[rgba(0,0,0,0.6)]', 'tw-text-[rgba(0,0,0,0.6)]');
      tab.classList.add('tw-bg-white', 'tw-border-primary', 'tw-text-primary');
      tab.setAttribute('aria-selected', 'true');

      // Show loading state
      const productGrid = sectionEl.querySelector(`.govx-collection-products-${sectionId}`);
      if (productGrid) {
        productGrid.style.opacity = '0.5';
        productGrid.style.pointerEvents = 'none';
      }

      try {
        // Fetch section from collection template
        const url = `/collections/${collectionHandle}?template=govx&section_id=${sectionId}`;
        const response = await fetch(url);
        const html = await response.text();
        
        // Parse the response
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSection = doc.querySelector(`.section.govx-collection-list`);
        
        if (newSection) {
          // Replace products container
          const newProductGrid = newSection.querySelector(`.govx-collection-products-${sectionId}`);
          if (newProductGrid && productGrid) {
            productGrid.innerHTML = newProductGrid.innerHTML;
            
            // Re-initialize only the load more button (tabs are handled by event delegation)
            initLoadMoreButton(sectionEl, sectionId);
          }
        }
      } catch (error) {
        console.error('Error loading collection:', error);
      } finally {
        // Remove loading state
        if (productGrid) {
          productGrid.style.opacity = '1';
          productGrid.style.pointerEvents = 'auto';
        }
      }
    });

    // Initialize load more button
    initLoadMoreButton(sectionEl, sectionId);
  });
}

/**
 * Re-initialize section after DOM update (e.g., after tab switch)
 * This only re-initializes the load more button, not the tabs
 */
function reinitGovxCollectionListSection(sectionId) {
  const sectionEl = document.querySelector(`.section.govx-collection-list[data-section-id="${sectionId}"]`);
  if (!sectionEl) return;
  
  // Only re-initialize load more button
  initLoadMoreButton(sectionEl, sectionId);
}

/**
 * Initialize load more button functionality
 */
function initLoadMoreButton(sectionEl, sectionId) {
  const loadMoreBtn = sectionEl.querySelector(`.govx-load-more-${sectionId}`);
  if (!loadMoreBtn) return;

  // Remove existing event listeners by cloning
  const newBtn = loadMoreBtn.cloneNode(true);
  loadMoreBtn.parentNode.replaceChild(newBtn, loadMoreBtn);

  newBtn.addEventListener('click', async function(e) {
    e.preventDefault();
    
    const nextPage = parseInt(this.dataset.nextPage) || 2;
    const collectionHandle = this.dataset.collectionHandle;
    if (!collectionHandle) return;

    // Disable button during load
    this.disabled = true;
    this.textContent = 'Loading...';

    try {
      // Fetch section from collection template with page parameter
      // Use section_id parameter for section rendering
      const url = `/collections/${collectionHandle}?view=govx&section_id=${sectionId}&page=${nextPage}`;
      const response = await fetch(url);
      const html = await response.text();
      
      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector(`.section.govx-collection-list`);
      
      if (newSection) {
        const currentProductGrid = sectionEl.querySelector(`[data-product-grid="${sectionId}"]`);
        const productContainer = newSection.querySelector(`.govx-collection-products-${sectionId}`);
        
        if (productContainer && currentProductGrid) {
          // For page 2+, products are directly in the container, not in a grid
          // Extract all product divs from the response
          const newProducts = Array.from(productContainer.querySelectorAll('.tw-w-full'));
          
          if (newProducts.length > 0) {
            // Append new products to current grid
            newProducts.forEach((product) => {
              currentProductGrid.appendChild(product);
            });
            
            // Update button state - check if we've reached the last page
            // We can estimate: if we got fewer than products_per_page, we're on the last page
            const productsPerPage = 8;
            if (newProducts.length < productsPerPage) {
              // No more pages, remove button
              this.remove();
            } else {
              // Update button for next page
              this.dataset.nextPage = nextPage + 1;
              this.disabled = false;
              this.textContent = 'LOAD MORE';
            }
          } else {
            // No products found, remove button
            this.remove();
          }
        } else {
          // Fallback: try to find product grid in response for full page renders
          const newProductGrid = newSection.querySelector(`[data-product-grid="${sectionId}"]`);
          if (newProductGrid && currentProductGrid) {
            const newProducts = Array.from(newProductGrid.children);
            newProducts.forEach((product) => {
              currentProductGrid.appendChild(product);
            });
            
            const totalPages = parseInt(newProductGrid.dataset.totalPages) || 1;
            const newCurrentPage = parseInt(newProductGrid.dataset.currentPage) || 1;
            
            if (newCurrentPage >= totalPages) {
              this.remove();
            } else {
              this.dataset.nextPage = newCurrentPage + 1;
              this.disabled = false;
              this.textContent = 'LOAD MORE';
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading more products:', error);
      this.disabled = false;
      this.textContent = 'LOAD MORE';
    }
  });
}

/**
 * Extract section ID from element
 */
function extractSectionId(element) {
  // Try data-section-id attribute
  if (element.dataset.sectionId) {
    return element.dataset.sectionId;
  }
  
  // Try id attribute like "shopify-section-123456"
  const id = element.id;
  if (id && id.startsWith('shopify-section-')) {
    return id.replace('shopify-section-', '');
  }
  
  // Try to find section ID from class names or data attributes
  const sectionIdAttr = element.querySelector('[data-section-id]');
  if (sectionIdAttr) {
    return sectionIdAttr.dataset.sectionId;
  }
  
  return null;
}

export default initGovxCollectionList;
export { reinitGovxCollectionListSection };

